import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Box,
  Divider,
  ToggleButton,
  ToggleButtonGroup,
  toggleButtonGroupClasses,
  ToggleButtonGroupProps,
  Typography,
  useTheme
} from "@mui/material";
import { CheckBox, CheckBoxOutlineBlank, ReportProblem } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { BookingServiceEditedData, ServiceOrderDto } from "../Domain/BookingServicePrompt.types";
import { renderRoomLayoutCell } from "../../../../Toolbars/InfoSidebar/ZoneInfoSidebar/RoomLayoutDialogContent/RoomLayoutTable.partial";
import { renderServiceImage } from "../../../../Toolbars/InfoSidebar/ZoneInfoSidebar/ServicePartial/ServiceCommonRender.partial";
import { EnlargedImageDialog } from "../../../../../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import { pickMandatoryRoomLayoutDefault } from "../functions/pickMandatoryRoomLayoutDefault.function";

type Props = {
  minHeight?: string;
  editedService: BookingServiceEditedData;
  setEditedService: Dispatch<SetStateAction<BookingServiceEditedData>>;
  orderedService: ServiceOrderDto;
  setOrderedService: Dispatch<SetStateAction<ServiceOrderDto>>;
  isMobile: boolean;
  isMandatory: boolean;
  isTabSelected: boolean;
};

/**
 * @version 0.1.0
 */
export default function BookingServiceRoomLayoutTable({
  minHeight = "60vh",
  editedService,
  orderedService,
  setOrderedService,
  isMobile,
  isMandatory,
  isTabSelected
}: Readonly<Props>) {
  const { t } = useTranslation();
  const theme = useTheme();

  const [selectedLayout, setSelectedLayout] = useState(0);
  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);

  const handleChange = (event: React.MouseEvent<HTMLElement>, newLayout: number) => {
    setSelectedLayout(newLayout);
    const selected = editedService.roomLayoutData.find(data => data.id === newLayout);

    if (newLayout === null) {
      setOrderedService(prev => ({ ...prev, roomLayout: [] }));
    } else
      setOrderedService(prev =>
        /* istanbul ignore next */
        ({
          ...prev,
          roomLayout: [
            {
              id: newLayout,
              name: selected?.name,
              setUpTime: selected?.setUpTime ?? 15,
              bookingLeadTime: selected?.bookingLeadTime ?? 1
            }
          ]
        })
      );
  };

  const isDataLoading = !editedService.roomLayoutData?.length;

  useEffect(() => {
    if (!isTabSelected) return;

    const layouts = editedService.roomLayoutData ?? [];

    if (orderedService.roomLayout.length > 0) {
      setSelectedLayout(orderedService.roomLayout[0].id);
      return;
    }

    if (!isMandatory || layouts.length === 0) {
      return;
    }

    const picked = pickMandatoryRoomLayoutDefault(layouts);
    if (picked == null) {
      return;
    }

    setSelectedLayout(picked.id);
    setOrderedService(prev => ({
      ...prev,
      roomLayout: [
        {
          id: picked.id,
          name: picked.name,
          setUpTime: picked.setUpTime ?? 15,
          bookingLeadTime: picked.bookingLeadTime ?? 1
        }
      ]
    }));
  }, [
    editedService.roomLayoutData,
    isMandatory,
    isTabSelected,
    orderedService.roomLayout,
    setOrderedService
  ]);

  return (
    <>
      <Box
        sx={[{ minHeight }, isMobile ? { overflowX: "auto" } : {}]}
        data-testid="room-layout-toggle-parent-box"
      >
        <StyledToggleButtonGroup
          color="primary"
          orientation={isMobile ? "vertical" : "horizontal"}
          value={selectedLayout}
          exclusive
          onChange={handleChange}
          aria-label="roomlayout"
          disabled={isDataLoading}
        >
          {isDataLoading ? (
            <Box sx={{ display: "flex", pl: 2 }}>
              <ReportProblem />
              <Typography variant={"body1"} sx={{ visibility: "visible", pl: 1 }}>
                {t("There are no available room layout")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ pl: 2 }} data-testid="room-layout-toggle-box">
              <Typography>{t("Layout")}</Typography>

              <Divider sx={{ opacity: "0.6" }} />

              <Box justifyItems={"flex-start"}>
                {editedService.roomLayoutData.map(item => (
                  <ToggleButton
                    value={item.id}
                    key={item.id}
                    // if it's not orderable, cannot select
                    disabled={!item.orderable}
                    data-testid={`toggle-layout-btn-${item.id}`}
                    sx={[
                      { flexDirection: "column", alignItems: "flex-start" },
                      isMobile ? { display: "flex" } : { display: "inline-flex" },
                      isMobile ? { width: "auto" } : { width: "100%" }
                    ]}
                  >
                    <Box
                      data-testid={`toggle-layout-box-${item.id}`}
                      sx={{ display: "flex", alignItems: "center", gap: 5 }}
                    >
                      {item.id === selectedLayout ? <CheckBox /> : <CheckBoxOutlineBlank />}

                      {renderRoomLayoutCell(item.layout, theme)}

                      {renderServiceImage(100, item.picturePath, setShowBiggerImage)}

                      <Box sx={{ width: 130, textAlign: "left" }}>
                        <Typography
                          sx={[isMobile ? { fontSize: "0.65rem" } : { fontSize: "0.75rem" }]}
                        >
                          {t(item.name)}
                        </Typography>
                      </Box>

                      <Box sx={{ width: "auto", textAlign: "left" }}>
                        <Typography
                          sx={[isMobile ? { fontSize: "0.65rem" } : { fontSize: "0.75rem" }]}
                        >
                          {t(item.description ?? "")}
                        </Typography>
                      </Box>
                    </Box>
                  </ToggleButton>
                ))}
              </Box>
            </Box>
          )}
        </StyledToggleButtonGroup>
      </Box>

      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </>
  );
}

export function StyledToggleButtonGroup(props: Readonly<ToggleButtonGroupProps>) {
  const theme = useTheme();

  return (
    <ToggleButtonGroup
      {...props}
      sx={{
        [`& .${toggleButtonGroupClasses.grouped}`]: {
          margin: theme.spacing(0.5),
          border: 0,
          borderRadius: theme.shape.borderRadius,
          [`&.${toggleButtonGroupClasses.disabled}`]: {
            border: 0
          }
        },
        [`& .${toggleButtonGroupClasses.middleButton}, & .${toggleButtonGroupClasses.lastButton}`]:
          {
            marginLeft: -1,
            borderLeft: "1px solid transparent"
          }
      }}
    />
  );
}
